import { CONNECTION_STATE } from '@/plugins/vueTwilioChat/constants'
import { LIVE_CHAT_STATUS, CLOSED_BY, MESSAGE_TYPES } from '@/enums'
import Api from '@/plugins/api'

const initialState = () => ({
  connectionStatus: CONNECTION_STATE.DISCONNECTED,
  userIdentity: '',
  channelSid: null,
  oldUserIdentity: '',
  liveChatStatus: LIVE_CHAT_STATUS.NOT_CONNECTED,
  sendByMail: false,
  participantLeft: false,
  agentAvailable: false,
  openingTime: false,
  contactToken: null, // used on bot escalation and queuing distribution
  welcomeMessageSent: false,
})

export const state = initialState()

export const getters = {
  authorizedToChat: (state, getters, rootState) =>
    !rootState.Widget.closeWidget.process &&
    !!state.userIdentity &&
    !!state.channelSid,
  readyToChat: (state) =>
    state.connectionStatus === CONNECTION_STATE.CONNECTED &&
    state.liveChatStatus === LIVE_CHAT_STATUS.READY &&
    !!state.userIdentity &&
    !!state.channelSid,
  displayWelcomeMessage: (state, getters, rootState, rootGetters) => {
    return (
      !rootGetters['Widget/isWidgetClosing'] &&
      !state.welcomeMessageSent &&
      rootState.labels.chatboxlabel?.chatbox_welcome_message &&
      !rootState.Bot.botEscalade &&
      !rootState.Bot.botWelcomeMessageSent
    )
  },
}

export const mutations = {
  RESET(state) {
    const newState = initialState()
    Object.keys(newState).forEach((key) => {
      state[key] = newState[key]
    })
  },
  UPDATE_CONNECTION_STATUS(state, payload) {
    state.connectionStatus = payload
  },
  UPDATE_USER_IDENTITY(state, payload) {
    state.userIdentity = payload
  },
  SET_OLD_USER_IDENTITY(state, payload) {
    state.oldUserIdentity = payload
  },
  RESET_OLD_USER_IDENTITY(state) {
    state.oldUserIdentity = initialState.oldUserIdentity
  },
  UPDATE_CHANNEL_SID(state, payload) {
    state.channelSid = payload
  },
  UPDATE_LIVE_CHAT_STATUS(state, payload) {
    state.liveChatStatus = payload
  },
  UPDATE_SEND_BY_MAIL(state, payload) {
    state.sendByMail = payload
  },
  UPDATE_AGENT_AVAILABLE(state, payload) {
    state.agentAvailable = payload
  },
  UPDATE_OPENING_TIME(state, payload) {
    state.openingTime = payload
  },
  UPDATE_CONTACT_TOKEN(state, payload = null) {
    state.contactToken = payload
  },
  UPDATE_WELCOME_MESSAGE_SENT(state, status) {
    state.welcomeMessageSent = status
  },
}

export const actions = {
  /**
   * Reset state
   * @param {*} { commit }
   */
  reset({ commit }) {
    commit('RESET')
  },
  /**
   * Fired when Client's connection state has been changed.
   * @param {String} payload connection state
   */
  CHAT_connectionStateChanged({ commit }, payload) {
    if (Object.values(CONNECTION_STATE).includes(payload)) {
      commit('UPDATE_CONNECTION_STATUS', payload)
    }
  },
  /**
   * Fired when the Client is subscribed to a User.
   * @param {Object<User>} payload
   */
  CHAT_userSubscribed({ commit }, payload) {
    commit('UPDATE_USER_IDENTITY', payload.identity)
  },
  /**
   * Fired when a Channel is no longer visible to the Client. Only fired for private channels.
   * @param {Object<Channel>} payload
   */
  CHAT_conversationRemoved({ commit, dispatch }) {
    commit('Widget/UPDATE_CLOSE_WIDGET', CLOSED_BY.AGENT, {
      root: true,
    })
    dispatch('removeCurrentUserIdentity')
  },
  updateLastTimestamp({ commit, rootGetters }, lastMessage) {
    const isNewLastMessage =
      lastMessage &&
      rootGetters['Messages/getLastMessage'].sid !== lastMessage.sid

    commit('Messages/UPDATE_LAST_MESSAGE_TIMESTAMP', lastMessage.timestamp, {
      root: true,
    })
  },
  updateUnreadMessages({ commit, rootGetters }, lastMessage) {
    const messageIsFromClient = lastMessage.attributes?.incoming
    if (messageIsFromClient) {
      commit('Messages/RESET_UNREAD_MESSAGES', null, { root: true })
    } else if (rootGetters['Messages/getLastMessage'].sid !== lastMessage.sid) {
      commit('Messages/INCREASE_UNREAD_MESSAGES', null, { root: true })
    }
  },
  async updatesChatMessages({ commit, dispatch }, payload) {
    // Manage conversation scroll behavior on livechat, scroll to bottom on client message
    const messageAddedByClient = payload?.attributes?.incoming
    commit('Messages/UPDATE_SCROLL_ALWAYS', messageAddedByClient, {
      root: true,
    })

    const messagesList = Array.isArray(payload) ? payload : [payload]

    const newMessages = await formatTwilioMessages(messagesList)
    const lastMessage = newMessages[newMessages.length - 1]

    dispatch('updateLastTimestamp', lastMessage)
    commit('TimeSync/UPDATE_TIME_SYNC', null, { root: true })
    dispatch('updateUnreadMessages', lastMessage)
    commit('Messages/UPDATE_MESSAGES_CHAT', newMessages, { root: true })
    dispatch('Widget/setLastAgentMessage', lastMessage, {
      root: true,
    })
  },
  async checkAgentAvailability(
    { rootState, commit, dispatch },
    fallback = true,
  ) {
    try {
      const {
        data: {
          data: { available, outOfSchedule },
        },
      } = await Api.chat.checkAvailability(rootState.skillId)
      commit('UPDATE_AGENT_AVAILABLE', available)
      commit('UPDATE_OPENING_TIME', !outOfSchedule)
      console.info(`checkAgentAvailability: ${available}`)

      if (!available && fallback) {
        dispatch('Widget/sessionTypeFallback', null, {
          root: true,
        })
      }
    } catch (e) {
      console.error(e)
    }
  },
  async checkOpeningTime({ commit }) {
    try {
      const {
        data: { data: isOpen = false },
      } = await Api.chat.checkChatOpen()
      commit('UPDATE_OPENING_TIME', isOpen)
      console.info(`checkOpeningTime: ${isOpen}`)
    } catch (e) {
      console.error(e)
    }
  },
  removeCurrentUserIdentity({ state, commit }) {
    if (!state.sendByMail) {
      commit('SET_OLD_USER_IDENTITY', state.userIdentity)
    }
    commit('UPDATE_USER_IDENTITY', '')
  },
  cleanLivechatConversation({ dispatch }) {
    dispatch('removeCurrentUserIdentity')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}

/**
 *
 * Extracted Logic
 *
 */

/**
 * Loop over media attached to the Twilio message
 * Extract media data to avoid to store Class on localstorage
 *
 * @param {Message} message : Message instance from Twilio
 * @returns {Array}
 */
async function extractMedia(media) {
  const list = media || []

  return Promise.all(
    list.map(async (media) => {
      return {
        url: await media.getContentTemporaryUrl(),
        contentType: media.contentType,
        sid: media.sid,
        filename: media.filename,
      }
    }),
  )
}

/**
 * Extract Twilio message data to avoid to store Class on localstorage
 *
 * @param {Message} message : Message instance from Twilio
 * @returns {Object}
 */
async function extractMessageInformations(message) {
  const { type, ...attributes } = message.attributes || {}

  const messageMedia = await extractMedia(message.attachedMedia)
  const messageType =
    type === MESSAGE_TYPES.SURVEY ? MESSAGE_TYPES.SURVEY : message.type
  const attributesType =
    type === MESSAGE_TYPES.RELAY_TO_AGENT
      ? MESSAGE_TYPES.RELAY_TO_AGENT
      : MESSAGE_TYPES.LIVE_CHAT

  return {
    timestamp: message.dateCreated,
    sid: message.sid,
    body: message.body,
    type: messageType,
    index: message.index,
    author: message.author,
    media: messageMedia,
    attributes: {
      ...attributes,
      type: attributesType,
    },
  }
}

/**
 * Transform incoming message from Twilio to our message Model
 *
 * @param {Array} messages : messages list from Twilio
 * @returns {Array}
 */
async function formatTwilioMessages(messages) {
  return Promise.all(
    messages.map(async (message) => await extractMessageInformations(message)),
  )
}
